import { AxiosError } from "axios"

import {
  PlacementRequestFeeType,
  type PlacementRequestFeeType as FeeType,
} from "openapi/api/model/placementRequestFeeType"
import {
  PlacementRequestPlacementType,
  type PlacementRequestPlacementType as PlacementType,
} from "openapi/api/model/placementRequestPlacementType"
import {
  PlacementSplitInputOwnerType,
  type PlacementSplitInputOwnerType as OwnerType,
} from "openapi/api/model/placementSplitInputOwnerType"

/**
 * What a placement has to be true for before it can be saved, kept as plain
 * functions so the dialog only has to render the answer.
 *
 * These used to be seven near-identical blocks inside the submit handler, each
 * with its own inline toast; the rules were hard to read past the toast
 * boilerplate and impossible to test without mounting the form.
 */

const VALID_PLACEMENT_TYPES = Object.values(PlacementRequestPlacementType) as PlacementType[]
const VALID_FEE_TYPES = Object.values(PlacementRequestFeeType) as FeeType[]
const VALID_OWNER_TYPES = Object.values(PlacementSplitInputOwnerType) as OwnerType[]

/** A split as the form holds it, with the percentage still a typed string. */
export type SplitDraft = {
  owner: string
  ownerType: OwnerType
  percentage: string
}

/** A split as the endpoint wants it. */
export type CleanSplit = {
  owner: string
  ownerType: OwnerType
  percentage: number
}

export type PlacementDraft = {
  jobCandidateId: string
  placementType: PlacementType
  feeType: FeeType
  splits: SplitDraft[]
}

export type PlacementProblem = { title: string; description: string }

export type PlacementValidation =
  | { ok: true; splits: CleanSplit[] }
  | { ok: false; problem: PlacementProblem }

/** Percentages are compared with a tolerance: 33.33 × 3 is not exactly 100. */
const SPLIT_TOLERANCE = 0.01

export function validatePlacement(draft: PlacementDraft): PlacementValidation {
  const fail = (title: string, description: string): PlacementValidation => ({
    ok: false,
    problem: { title, description },
  })

  if (!draft.jobCandidateId) {
    return fail(
      "Missing pipeline record",
      "Select a job and candidate before saving this placement.",
    )
  }

  if (!VALID_PLACEMENT_TYPES.includes(draft.placementType)) {
    return fail("Invalid placement type", "Choose a valid placement type before saving.")
  }

  if (!VALID_FEE_TYPES.includes(draft.feeType)) {
    return fail("Invalid fee type", "Choose a valid fee type before saving.")
  }

  const splits: CleanSplit[] = draft.splits.map((split) => ({
    owner: split.owner.trim(),
    ownerType: split.ownerType,
    percentage: Number(split.percentage || 0),
  }))

  if (splits.length === 0 || splits.some((split) => !split.owner)) {
    return fail("Splits are required", "Every split needs an owner name.")
  }

  if (splits.some((split) => !VALID_OWNER_TYPES.includes(split.ownerType))) {
    return fail("Invalid split", "Each split needs a valid owner type.")
  }

  if (splits.some((split) => !Number.isFinite(split.percentage))) {
    return fail("Invalid split", "Each split percentage must be a valid number.")
  }

  const total = splits.reduce((sum, split) => sum + split.percentage, 0)
  if (Math.abs(total - 100) > SPLIT_TOLERANCE) {
    return fail(
      "Splits do not add up",
      `The split percentages total ${formatPercent(total)}%, and must total 100%.`,
    )
  }

  return { ok: true, splits }
}

/** Whole numbers stay whole; 33.333… becomes 33.33. */
export function formatPercent(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2)
}

/**
 * What the fee is actually worth.
 *
 * `fee_amount` carries two different things depending on `fee_type`: a
 * percentage of salary, or a sum of money. Canvas 15a puts the money next to
 * the percentage — "fee is the one number the old form made you work out
 * yourself" — so the conversion lives here and both the dialog and the split
 * values read it.
 */
export function feeValue({
  fee,
  feeType,
  salary,
}: {
  fee: string
  feeType: FeeType
  salary: string
}): number | undefined {
  const feeNumber = Number(fee)
  if (!fee.trim() || !Number.isFinite(feeNumber)) return undefined
  if (feeType === "fixed") return feeNumber

  const salaryNumber = Number(salary)
  if (!salary.trim() || !Number.isFinite(salaryNumber)) return undefined
  return (salaryNumber * feeNumber) / 100
}

/**
 * Money as the canvas writes it: `$28,400`, no trailing cents on round sums.
 * The symbol is whatever the form's currency picker is set to, so this groups
 * the number itself rather than going through `Intl`'s currency formatting,
 * which would need an ISO code the form does not collect.
 */
export function formatMoney(value: number | undefined, currency = "$"): string {
  if (value === undefined || !Number.isFinite(value)) return "—"
  return `${currency}${value.toLocaleString(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  })}`
}

/**
 * The API returns its validation errors as a JSON string inside `error`, so the
 * message a recruiter needs is two layers down. Anything unreadable falls back
 * to a plain sentence rather than showing raw JSON.
 */
export function describePlacementFailure(error: unknown): string {
  const raw = (error as AxiosError<{ error?: unknown }>)?.response?.data?.error
  if (typeof raw !== "string") return "Something went wrong."

  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed[0]?.message) return parsed[0].message
    if (parsed?.message) return parsed.message
    return raw
  } catch {
    return raw
  }
}
